import type {
  Action,
  Card,
  CardTemplate,
  Die,
  DieFaces,
  GameState,
} from '../types';
import { ROW_WIDTHS } from '../types';
import { rowWidth, isAcquirable, findCard } from './adjacency';
import { isActionAllowed } from './stateMachine';
import { createRng, type Rng } from './rng';
import { applyRewards, createDie } from './rewards';

export const HAND_SIZE = 3;

export interface NewGameOptions {
  seed?: number;
  cardPools: CardTemplate[][];
  startingBag: Die[];
}

export function diceFaceMax(faces: DieFaces): number {
  return faces;
}

function rollDie(die: Die, rng: Rng): Die {
  const max = diceFaceMax(die.faces);
  return { ...die, value: rng.intInRange(1, max) };
}

function applyAdjustment(die: Die, delta: 1 | -1): Die | null {
  if (die.value == null) return null;
  const max = diceFaceMax(die.faces);
  const next = die.value + delta;
  if (next < 1 || next > max) return null;
  return { ...die, value: next };
}

export function buildPyramid(
  pools: CardTemplate[][],
  rng: Rng
): Card[] {
  if (pools.length !== ROW_WIDTHS.length) {
    throw new Error(`Expected ${ROW_WIDTHS.length} pools, got ${pools.length}`);
  }
  const cards: Card[] = [];
  for (let r = 0; r < ROW_WIDTHS.length; r++) {
    const w = rowWidth(r);
    const pool = pools[r]!;
    if (pool.length < w) {
      throw new Error(`Pool ${r} has ${pool.length} cards, needs ≥ ${w}`);
    }
    const drawn = rng.sample(pool, w);
    drawn.forEach((tpl, c) => {
      cards.push({
        id: `${tpl.id}@${r}:${c}`,
        row: r as 0 | 1 | 2 | 3 | 4,
        col: c,
        cost: tpl.cost,
        rewards: tpl.rewards,
        placedDice: [],
        acquired: false,
      });
    });
  }
  return cards;
}

export function defaultStartingBag(): Die[] {
  const dice: Die[] = [];
  for (let i = 0; i < 6; i++) dice.push(createDie('white'));
  dice.push(createDie('red'));
  dice.push(createDie('blue'));
  return dice;
}

export function newGame(opts: NewGameOptions): GameState {
  const seed = opts.seed ?? Math.floor(Math.random() * 2 ** 31);
  const rng = createRng(seed);
  const pyramid = buildPyramid(opts.cardPools, rng);
  return {
    bag: opts.startingBag.slice(),
    hand: [],
    pyramid,
    lastAcquired: null,
    acquiredThisTurn: 0,
    tokens: { reroll: 0, convertColor: 0 },
    perksRemaining: { adjust: 0, reroll: 0 },
    phase: 'Drawing',
    rngState: rng.state,
    seed,
  };
}

function withRng<T>(state: GameState, fn: (rng: Rng) => T): { value: T; rngState: number } {
  const rng = createRng(state.rngState);
  const value = fn(rng);
  return { value, rngState: rng.state };
}

function findAndUpdateCard(
  pyramid: Card[],
  cardId: string,
  fn: (c: Card) => Card
): Card[] {
  return pyramid.map((c) => (c.id === cardId ? fn(c) : c));
}

function findAndUpdateDieInHand(
  hand: Die[],
  dieId: string,
  fn: (d: Die) => Die
): Die[] | null {
  const idx = hand.findIndex((d) => d.id === dieId);
  if (idx === -1) return null;
  const next = hand.slice();
  next[idx] = fn(hand[idx]!);
  return next;
}

function lowestValueDice(dice: Die[]): Die[] {
  if (dice.length === 0) return [];
  let min = Infinity;
  for (const d of dice) if (d.value != null && d.value < min) min = d.value;
  return dice.filter((d) => d.value === min);
}

function startTurn(state: GameState): GameState {
  if (state.bag.length === 0) {
    return { ...state, phase: 'Loss' };
  }
  const drawCount = Math.min(HAND_SIZE, state.bag.length);
  // Draw using seeded RNG
  const { value: drawn, rngState } = withRng(state, (rng) => {
    return rng.sample(state.bag, drawCount);
  });
  const drawnIds = new Set(drawn.map((d) => d.id));
  const remainingBag = state.bag.filter((d) => !drawnIds.has(d.id));
  const adjust = drawn.filter((d) => d.color === 'red').length;
  const reroll = drawn.filter((d) => d.color === 'blue').length;
  return {
    ...state,
    bag: remainingBag,
    hand: drawn,
    perksRemaining: { adjust, reroll },
    acquiredThisTurn: 0,
    phase: 'Rolling',
    rngState,
  };
}

function rollHand(state: GameState): GameState {
  const { value: rolled, rngState } = withRng(state, (rng) =>
    state.hand.map((d) => rollDie(d, rng))
  );
  return { ...state, hand: rolled, rngState, phase: 'AdjustingPerks' };
}

function adjustDie(state: GameState, dieId: string, delta: 1 | -1): GameState {
  if (state.perksRemaining.adjust <= 0) return state;
  const idx = state.hand.findIndex((d) => d.id === dieId);
  if (idx === -1) return state;
  const adjusted = applyAdjustment(state.hand[idx]!, delta);
  if (!adjusted) return state;
  const hand = state.hand.slice();
  hand[idx] = adjusted;
  const phase = state.phase === 'AdjustingPerks' ? 'Placing' : state.phase;
  return {
    ...state,
    hand,
    perksRemaining: {
      ...state.perksRemaining,
      adjust: state.perksRemaining.adjust - 1,
    },
    phase,
  };
}

function rerollDie(state: GameState, dieId: string): GameState {
  if (state.perksRemaining.reroll <= 0) return state;
  const idx = state.hand.findIndex((d) => d.id === dieId);
  if (idx === -1) return state;
  const { value: nextDie, rngState } = withRng(state, (rng) =>
    rollDie(state.hand[idx]!, rng)
  );
  const hand = state.hand.slice();
  hand[idx] = nextDie;
  const phase = state.phase === 'AdjustingPerks' ? 'Placing' : state.phase;
  return {
    ...state,
    hand,
    rngState,
    perksRemaining: {
      ...state.perksRemaining,
      reroll: state.perksRemaining.reroll - 1,
    },
    phase,
  };
}

function placeDie(state: GameState, dieId: string, cardId: string): GameState {
  const card = state.pyramid.find((c) => c.id === cardId);
  if (!card || card.acquired) return state;
  const die = state.hand.find((d) => d.id === dieId);
  if (!die || die.value == null) return state;
  const hand = state.hand.filter((d) => d.id !== dieId);
  const pyramid = findAndUpdateCard(state.pyramid, cardId, (c) => ({
    ...c,
    placedDice: [...c.placedDice, die],
  }));
  return { ...state, hand, pyramid, phase: 'Placing' };
}

function unplaceDie(state: GameState, dieId: string): GameState {
  if (state.acquiredThisTurn > 0) return state;
  let foundDie: Die | null = null;
  const pyramid = state.pyramid.map((c) => {
    if (foundDie) return c;
    const idx = c.placedDice.findIndex((d) => d.id === dieId);
    if (idx === -1) return c;
    foundDie = c.placedDice[idx]!;
    const next = c.placedDice.slice();
    next.splice(idx, 1);
    return { ...c, placedDice: next };
  });
  if (!foundDie) return state;
  return { ...state, hand: [...state.hand, foundDie], pyramid };
}

function finishPlacing(state: GameState): GameState {
  if (state.hand.length > 0) return state;
  return { ...state, phase: 'Resolving' };
}

function acquireCard(
  state: GameState,
  cardId: string,
  tieBreakerDieId?: string
): GameState {
  const card = state.pyramid.find((c) => c.id === cardId);
  if (!card) return state;
  if (!isAcquirable(card, state)) return state;

  const tied = lowestValueDice(card.placedDice);
  let lockedDie: Die;
  if (tied.length === 1) {
    lockedDie = tied[0]!;
  } else {
    if (!tieBreakerDieId) return state;
    const chosen = tied.find((d) => d.id === tieBreakerDieId);
    if (!chosen) return state;
    lockedDie = chosen;
  }

  const returnedDice = card.placedDice
    .filter((d) => d.id !== lockedDie.id)
    .map<Die>((d) => ({ ...d, value: null }));

  const pyramid = findAndUpdateCard(state.pyramid, cardId, (c) => ({
    ...c,
    placedDice: [lockedDie],
    acquired: true,
  }));

  let next: GameState = {
    ...state,
    bag: [...state.bag, ...returnedDice],
    pyramid,
    lastAcquired: { row: card.row, col: card.col },
    acquiredThisTurn: state.acquiredThisTurn + 1,
  };

  const rng = createRng(next.rngState);
  next = applyRewards(next, card.rewards, rng);
  next = { ...next, rngState: rng.state };

  if (card.row === 4) {
    return { ...next, phase: 'Win' };
  }
  return next;
}

function endTurn(state: GameState): GameState {
  if (state.hand.length > 0) return state;
  if (state.bag.length === 0) {
    return { ...state, phase: 'Loss' };
  }
  return { ...state, phase: 'Drawing' };
}

function useToken(
  state: GameState,
  token: 'reroll' | 'convertColor',
  dieId: string,
  toColor?: Die['color']
): GameState {
  if ((state.tokens[token] ?? 0) <= 0) return state;
  if (token === 'reroll') {
    const idx = state.hand.findIndex((d) => d.id === dieId);
    if (idx === -1) return state;
    const { value: rolled, rngState } = withRng(state, (rng) =>
      rollDie(state.hand[idx]!, rng)
    );
    const hand = state.hand.slice();
    hand[idx] = rolled;
    return {
      ...state,
      hand,
      rngState,
      tokens: { ...state.tokens, reroll: state.tokens.reroll - 1 },
    };
  } else {
    if (!toColor) return state;
    const next = findAndUpdateDieInHand(state.hand, dieId, (d) => ({
      ...d,
      color: toColor,
    }));
    if (!next) return state;
    return {
      ...state,
      hand: next,
      tokens: { ...state.tokens, convertColor: state.tokens.convertColor - 1 },
    };
  }
}

export function reduce(state: GameState, action: Action): GameState {
  if (!isActionAllowed(state.phase, action.type)) return state;
  switch (action.type) {
    case 'newGame':
      // Caller should use newGame() factory; this branch is a no-op for safety.
      return state;
    case 'startTurn':
      return startTurn(state);
    case 'roll':
      return rollHand(state);
    case 'adjustDie':
      return adjustDie(state, action.dieId, action.delta);
    case 'rerollDie':
      return rerollDie(state, action.dieId);
    case 'placeDie':
      return placeDie(state, action.dieId, action.cardId);
    case 'unplaceDie':
      return unplaceDie(state, action.dieId);
    case 'finishPlacing':
      return finishPlacing(state);
    case 'acquireCard':
      return acquireCard(state, action.cardId, action.tieBreakerDieId);
    case 'endTurn':
      return endTurn(state);
    case 'useToken':
      return useToken(state, action.token, action.dieId, action.toColor);
  }
}

export function tieBreakerNeeded(card: Card): boolean {
  return lowestValueDice(card.placedDice).length > 1;
}

export function lowestTiedDice(card: Card): Die[] {
  return lowestValueDice(card.placedDice);
}

// Re-export so consumers don't need a separate import path
export { findCard };
